import groupBy from 'lodash-es/groupBy';
import get from 'lodash-es/get';
import { saveAs } from 'file-saver';
import { AxiosError } from 'axios';
import { UseQueryOptions } from 'react-query';

export type QueryOptions = Omit<UseQueryOptions<any, any, any, any>, 'queryFn' | 'queryKey'>;

/**
 * Returns the pages data from the paginated list
 *
 * @param {} data the API response object
 * @param {} keyPath it can be a dot notation path
 */
export const filterInfiniteQueryData = (data, keyPath) => {
  // InfiniteQuery data format: {pageParams: [], pages: [{dataKey:[]}]}
  if (!data?.pages?.length) {
    return [];
  }

  return data.pages.reduce((pages, page) => {
    const pageData = get(page, keyPath);
    return pageData ? [...pages, ...pageData] : pages;
  }, []);
};

/**
 * Async Timeout/Delay Function
 * @param time
 */
export const waitFor = (time: number) => {
  return new Promise((resolve, reject) => {
    if (isNaN(time)) {
      return reject(new Error('Parameter must be a number'));
    }
    setTimeout(resolve, time);
  });
};

/**
 * Handler for sending custom events to Pendo
 * @param eventName
 * @param params
 */ export const sendEventTrack = (eventName, params) => {
  try {
    if (typeof window['pendo'] !== 'undefined' && eventName) {
      pendo?.track(eventName, params);
    }
  } catch (e) {
    console.error('Pendo error: ', e);
  }
};

// used as a separator for joined roles
export const ROLE_SEPARATOR = ', ';

/**
 * Merge SAML accounts with same ID and multiple roles into one.
 *
 * @param {CloudOneAccount[]} accounts
 * @returns {CloudOneAccount[]}
 */
export const mergeSAMLAccountsById = (accounts: CloudOneAccount[]): CloudOneAccount[] => {
  const accountsById = groupBy(accounts, 'id');

  const mergedAccounts: CloudOneAccount[] = [];
  for (const [account, ...restOfAccounts] of Object.values(accountsById)) {
    mergedAccounts.push({
      ...account,
      roles: [account.roleID, ...restOfAccounts.map(({ roleID }) => roleID)],
    });
  }
  return mergedAccounts;
};

/**
 * Returns true if a given account has multiple roles.
 *
 * @param {CloudOneAccount}
 * @returns {boolean}
 */
export const hasMultipleRoles = ({ roles = [] }: CloudOneAccount): boolean => {
  return roles.length > 1;
};

/**
 * Returns a comma-separated list of roleIDs or the account roleID.
 *
 * @param {CloudOneAccount} account
 * @returns {string}
 */
export const getAccountsRoles = (account: CloudOneAccount): string => {
  if (hasMultipleRoles(account)) {
    return account?.roles.join(ROLE_SEPARATOR);
  }

  return account.roleID;
};

/**
 * Copies the given content in the clipboard.
 *
 * @param {string} content
 * @returns {Promise<boolean>}
 */
export const copyToClipboard = async (content: string): Promise<boolean> => {
  if (!content) {
    console.warn('No value provided to the clipboard');
    return false;
  }
  try {
    await navigator.clipboard.writeText(content);
    return true;
  } catch (error) {
    console.error('Copy to clipboard failed: ', error);
    return false;
  }
};

/**
 * Downloads the given content as a text file
 *
 * @param {string} content
 * @param {string} filename
 * @returns {void}
 */
export const downloadTextAsFile = (content: string, filename?: string): void => {
  if (!content) {
    console.error('No content to download');
    return;
  }
  saveAs(
    new Blob([content], { type: 'text/plain;charset=utf-8' }),
    `${filename || `${+Date.now()}`}.txt`
  );
};

// Reads the c1 token from the sessions storage
export const getC1Token = (): string => {
  const c1Token = JSON.parse(sessionStorage?.C1C_Shell_State || '{}')?.c1Token?.token;

  if (!c1Token) {
    throw new Error('getC1Token(): cannot get c1Token');
  }

  return c1Token;
};

export const openLink = (url: string, openInNewTab = false) => {
  const newWindow = window.open(url, openInNewTab ? '_blank' : '_self', 'noopener,noreferrer');
  if (newWindow?.opener) newWindow.opener = null; // maintains backwards compatibility for browsers that don't support "noopener" above
};

// Helper to handle Axios API  call error
export const throwAxiosError = (
  { response, request, message: errorMessage }: AxiosError,
  apiName: string = 'API Error'
): { message: string; status?: number } => {
  // https://axios-http.com/docs/handling_errors
  const message: string = `${response?.data?.message || errorMessage || request}`;
  console.error(`${apiName}: ${message}`);
  throw { message, status: response?.status };
};

// TODO: Remove the FeatureFlag check with C1C-11968
const checkGovCloudRegion = (): boolean => {
  const isGovCloudDeploymentTarget = ['govcloud-prod', 'govcloud-staging'].includes(
    DEPLOYMENT_TARGET
  );
  const hasGovCloudFeatureFlag = JSON.parse(
    localStorage.getItem('C1C_GOV_CLOUD_ENABLED') || 'false'
  );
  return isGovCloudDeploymentTarget || hasGovCloudFeatureFlag;
};
export const isGovCloudRegion = checkGovCloudRegion();

export const getV1PortalURL = (): string => {
  switch (DEPLOYMENT_TARGET) {
    case 'alpha':
    case 'dev':
      return 'https://signin-int.visionone.trendmicro.com/';
    case 'stage':
      return 'https://signin-xdr.visionone.trendmicro.com/';
    case 'prod':
    default:
      return 'https://signin.v1.trendmicro.com/';
  }
};

export const openInNewTab = (url: string) => {
  return window.open(url, '_blank', 'noopener,noreferrer');
};

export const hasProdLogsEnabled = JSON.parse(
  localStorage.getItem('C1C_PROD_LOGS_ENABLED') || 'false'
);
